import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { SettingsProfileCard } from "../components/settings/SettingsProfileCard";
import { SettingsBoutiqueInfo } from "../components/settings/SettingsBoutiqueInfo";
import { SettingsCodeAccess } from "../components/settings/SettingsCodeAccess";
import { SettingsDeletionPin } from "../components/settings/SettingsDeletionPin";
import { SettingsInvoice } from "../components/settings/SettingsInvoice";
import { SettingsNotifications } from "../components/settings/SettingsNotifications";
import { SettingsStyles } from "../components/settings/SettingsStyles";
import { SettingsSeo } from "../components/settings/SettingsSeo";
import { SettingsPayments } from "../components/settings/SettingsPayments";
import { getFeatures } from "../lib/boutiqueType";

const CURRENCIES = [
  { code: "EUR", symbol: "\u20ac", label: "EUR \u20ac" },
  { code: "XOF", symbol: "F CFA", label: "XOF F CFA" },
  { code: "USD", symbol: "$", label: "USD $" },
  { code: "GBP", symbol: "\u00a3", label: "GBP \u00a3" },
  { code: "GHS", symbol: "GH\u20b5", label: "GHS GH\u20b5" },
  { code: "MAD", symbol: "MAD", label: "MAD" },
];

export default function Settings() {
  const { getAuthHeaders, user } = useAuth();
  const { updateSidebarImage, isDark } = useOutletContext() || {};
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [logoUrl, setLogoUrl] = useState("");
  const [profilePhoto, setProfilePhoto] = useState("");
  const [boutiqueName, setBoutiqueName] = useState("");
  const [plan, setPlan] = useState("starter");
  const [boutiqueSlug, setBoutiqueSlug] = useState("");
  const [codeAcces, setCodeAcces] = useState("");
  const [info, setInfo] = useState({ boutique_name: "", telephone: "", emailContact: "", adresse: "", devise: "EUR" });
  const [invoice, setInvoice] = useState({ store_name: "", store_address: "", store_phone: "", store_email: "", tva_number: "", siret: "" });
  const [notifs, setNotifs] = useState({ low_stock: true, new_orders: true, daily_sales: false, weekly_summary: false });

  const boutiqueData = JSON.parse(localStorage.getItem("boutique") || "{}");
  const accent = boutiqueData.customColor || "#FF4500";
  const features = getFeatures();
  const vitrineUrl = (features.vitrine && boutiqueSlug) ? `${window.location.origin}/vitrine/${boutiqueSlug}` : "";

  useEffect(() => {
    const loadAll = async () => {
      try {
        const headers = getAuthHeaders();
        const [profileRes, codeRes, settingsRes] = await Promise.allSettled([
          axios.get(`${API}/settings/profile`, headers),
          axios.get(`${API}/settings/boutique-code`, headers),
          axios.get(`${API}/settings`, headers),
        ]);

        if (profileRes.status === "fulfilled") {
          const p = profileRes.value.data;
          setLogoUrl(p.logo_url || "");
          setProfilePhoto(p.profile_photo || "");
          setBoutiqueName(p.boutique_name || boutiqueData.nom || "");
          setPlan(p.plan || "starter");
          setBoutiqueSlug(p.slug || boutiqueData.slug || "");
          if (p.notifications) setNotifs(p.notifications);
        }

        if (codeRes.status === "fulfilled") {
          setCodeAcces(codeRes.value.data.code_acces || "");
        }

        if (settingsRes.status === "fulfilled") {
          const s = settingsRes.value.data;
          setInvoice({
            store_name: s.store_name || "",
            store_address: s.store_address || "",
            store_phone: s.store_phone || "",
            store_email: s.store_email || "",
            tva_number: s.tva_number || "",
            siret: s.siret || "",
          });
        }

        const slug = boutiqueData.slug || "";
        if (slug) {
          try {
            const bRes = await axios.get(`${API}/boutiqueos/boutique/${slug}`);
            const d = bRes.data;
            const infos = {
              boutique_name: d.boutique_name || "",
              telephone: d.telephone || d.whatsapp || "",
              emailContact: d.emailContact || d.owner_email || "",
              adresse: d.adresse || "",
              devise: boutiqueData.currency || "EUR",
            };
            setInfo(infos);
          } catch {}
        } else {
          setInfo(prev => ({ ...prev, boutique_name: boutiqueData.nom || "", devise: boutiqueData.currency || "EUR" }));
        }
      } catch {} finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  const handleImageUpload = async (file, type) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("L'image ne doit pas depasser 2 Mo"); return; }
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result;
      try {
        if (type === "logo") {
          await axios.post(`${API}/settings/logo`, { image: base64 }, getAuthHeaders());
          setLogoUrl(base64);
          updateSidebarImage?.("logo", base64);
          toast.success("Logo mis a jour");
        } else {
          await axios.post(`${API}/settings/profile-photo`, { image: base64 }, getAuthHeaders());
          setProfilePhoto(base64);
          updateSidebarImage?.("photo", base64);
          toast.success("Photo de profil mise a jour");
        }
      } catch { toast.error("Erreur lors de l'upload"); }
    };
    reader.readAsDataURL(file);
  };

  const handleSaveBoutiqueInfo = async (draft) => {
    if (!draft.boutique_name.trim()) { toast.error("Le nom est obligatoire"); return; }
    try {
      const slug = boutiqueData.slug || boutiqueSlug;
      const cur = CURRENCIES.find(c => c.code === draft.devise);
      if (slug) {
        await axios.put(`${API}/boutiqueos/boutique/${slug}`, {
          boutique_name: draft.boutique_name,
          telephone: draft.telephone,
          emailContact: draft.emailContact,
          adresse: draft.adresse,
          currency: draft.devise,
          currency_symbol: cur?.symbol || draft.devise,
        });
      }
      // Update localStorage cache
      const cached = JSON.parse(localStorage.getItem("boutique") || "{}");
      cached.currency = draft.devise;
      cached.currencySymbol = cur?.symbol || draft.devise;
      localStorage.setItem("boutique", JSON.stringify(cached));
      setInfo({ ...draft });
      toast.success("Informations boutique mises a jour");
    } catch { toast.error("Erreur lors de la sauvegarde"); }
  };

  const handleRegenerate = async () => {
    try {
      const res = await axios.post(`${API}/settings/boutique-code/regenerate`, {}, getAuthHeaders());
      setCodeAcces(res.data.code_acces);
      toast.success("Code regenere");
    } catch { toast.error("Erreur"); }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await Promise.all([
        axios.put(`${API}/settings/invoice-info`, invoice, getAuthHeaders()),
        axios.put(`${API}/settings/notifications`, notifs, getAuthHeaders()),
      ]);
      toast.success("Parametres enregistres");
    } catch { toast.error("Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <div className="settings-page" data-testid="settings-page">
      <div className="s-header">
        <div>
          <h1 className="s-title">Parametres</h1>
          <p className="s-subtitle">Gerez votre boutique et personnalisez votre profil</p>
        </div>
        <button className="s-save-top" onClick={handleSaveAll} disabled={saving} data-testid="save-settings-top">
          {saving ? <Loader2 size={16} className="animate-spin" /> : "Enregistrer"}
        </button>
      </div>

      <SettingsProfileCard
        logoUrl={logoUrl}
        profilePhoto={profilePhoto}
        boutiqueName={boutiqueName}
        plan={plan}
        vitrineUrl={vitrineUrl}
        user={user}
        isDark={isDark}
        accent={accent}
        onImageUpload={handleImageUpload}
      />

      <SettingsBoutiqueInfo info={info} accent={accent} isDark={isDark} onSave={handleSaveBoutiqueInfo} />

      <SettingsPayments getAuthHeaders={getAuthHeaders} accent={accent} />

      {features.customers && (
      <SettingsCodeAccess codeAcces={codeAcces} boutiqueName={info.boutique_name} onRegenerate={handleRegenerate} />
      )}

      {features.vitrine && (
      <SettingsSeo getAuthHeaders={getAuthHeaders} accent={accent} isDark={isDark} />
      )}

      <SettingsDeletionPin getAuthHeaders={getAuthHeaders} accent={accent} />

      <SettingsInvoice invoice={invoice} onChange={setInvoice} />

      <SettingsNotifications notifs={notifs} accent={accent} onChange={setNotifs} />

      <button className="s-save-bottom" onClick={handleSaveAll} disabled={saving} data-testid="save-settings-btn">
        {saving ? <Loader2 size={16} className="animate-spin" /> : "Enregistrer les parametres"}
      </button>

      <SettingsStyles isDark={isDark} accent={accent} />
    </div>
  );
}
